import type { Invoice } from '@stayafrica/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  NoPermissionState,
  Notice,
  PageHeader,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  toast,
} from '@stayafrica/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  Banknote,
  Download,
  KeyRound,
  Lock,
  Mail,
  Printer,
  RotateCcw,
  Save,
  ShieldCheck,
  Upload,
  Wallet,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import {
  BillingDocumentPreview,
  BillingDocumentScroller,
} from '@/features/billing/BillingDocumentPreview';
import {
  BillingDraftEditor,
  newDraftLine,
  type BillingDraftState,
} from '@/features/billing/BillingDraftEditor';
import { ComplianceBlockPanel, ComplianceOverrideDrawer } from '@/features/billing/ComplianceGate';
import { CreditNoteDrawer } from '@/features/billing/CreditNoteDrawer';
import { invoicePreviewProps } from '@/features/billing/preview';
import { SendDocumentDialog } from '@/features/billing/SendDocumentDialog';
import { PaymentDrawer } from '@/features/payments/PaymentDrawer';
import { methodTone, paymentBadge } from '@/features/payments/status';
import {
  billingKeys,
  billingSettingsQueryOptions,
  invoicePaymentsQueryOptions,
  invoicePdf,
  invoiceQueryOptions,
  issueInvoice,
  reservationBillingQueryOptions,
  updateInvoice,
} from '@/features/billing/queries';
import { invoiceActions, invoiceTone, overdueDays } from '@/features/billing/status';
import { computeBillingTotals } from '@/features/billing/totals';
import { optionalWithholding, taxRulesQueryOptions } from '@/features/tax/queries';
import { ApiRequestError, openDownloadedFile } from '@/lib/api';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateShort, formatDateTime } from '@/lib/format';

export const Route = createFileRoute('/_app/billing/invoices/$invoiceId')({
  component: InvoiceDetailPage,
});

function draftFrom(invoice: Invoice): BillingDraftState {
  return {
    subject: invoice.subject ?? '',
    lines: invoice.lines.map((line) =>
      newDraftLine({
        kind: line.kind,
        label: line.label,
        description: line.description ?? '',
        quantity: line.quantity,
        unitPrice: line.unitPrice,
      }),
    ),
    discountType: invoice.totals.discountType,
    discountValue: invoice.totals.discountValue,
    applyWithholding: invoice.totals.withholdingApplied,
    notes: invoice.notes ?? '',
    dueDate: invoice.dueDate ?? '',
  };
}

function InvoiceDetailPage() {
  const { invoiceId } = Route.useParams();
  const { t, i18n } = useTranslation(['billing', 'payments', 'common', 'documents', 'errors']);
  const queryClient = useQueryClient();
  const session = useSession();
  const canRead = session.can('invoice.read');

  const query = useQuery(invoiceQueryOptions(invoiceId, canRead));
  const settings = useQuery(billingSettingsQueryOptions(canRead));
  const taxRules = useQuery(taxRulesQueryOptions({ kind: 'WITHHOLDING' }, canRead));
  const withholdingRule = optionalWithholding(taxRules.data);
  const invoice = query.data;

  const payments = useQuery(
    invoicePaymentsQueryOptions(
      invoiceId,
      canRead && session.can('payment.read') && invoice?.status !== 'DRAFT',
    ),
  );
  const summary = useQuery(
    reservationBillingQueryOptions(invoice?.reservationId ?? '', Boolean(invoice?.reservationId)),
  );

  const [draft, setDraft] = useState<BillingDraftState | null>(null);
  const [dirty, setDirty] = useState(false);
  const [confirmIssue, setConfirmIssue] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [creditNoteOpen, setCreditNoteOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [serverBlocked, setServerBlocked] = useState<string[] | null>(null);

  useEffect(() => {
    if (!invoice) return;
    setDraft(draftFrom(invoice));
    setDirty(false);
  }, [invoice?.id, invoice?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const liveTotals = useMemo(() => {
    if (!draft) return null;
    return computeBillingTotals({
      lines: draft.lines,
      discountType: draft.discountType,
      discountValue: draft.discountValue,
      applyWithholding: draft.applyWithholding,
      ...(withholdingRule ? { withholdingRate: withholdingRule.rate } : {}),
      paidTotal: invoice?.totals.paidTotal ?? 0,
      locale: i18n.language,
    });
  }, [draft, withholdingRule, invoice?.totals.paidTotal, i18n.language]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: billingKeys.all });
  };

  const save = useMutation({
    mutationFn: () => {
      if (!invoice || !draft) throw new Error('missing');
      return updateInvoice(invoice.id, {
        version: invoice.version,
        subject: draft.subject || null,
        lines: draft.lines.map((line) => ({
          kind: line.kind,
          label: line.label,
          description: line.description || null,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
        })),
        discountType: draft.discountType,
        discountValue: draft.discountValue,
        applyWithholding: draft.applyWithholding,
        notes: draft.notes || null,
        ...(draft.dueDate ? { dueDate: draft.dueDate } : {}),
      });
    },
    onSuccess: async () => {
      await invalidate();
      setDirty(false);
      toast.success(t('billing:toasts.invoiceUpdated'));
    },
  });

  const issue = useMutation({
    mutationFn: (override?: { reason: string; documentsExpectedBy: string | null }) => {
      if (!invoice) throw new Error('missing');
      return issueInvoice(
        invoice.id,
        invoice.version,
        override
          ? { reason: override.reason, documentsExpectedBy: override.documentsExpectedBy }
          : undefined,
      );
    },
    onSuccess: async (issued, override) => {
      await invalidate();
      setConfirmIssue(false);
      setOverrideOpen(false);
      setServerBlocked(null);
      toast.success(
        override
          ? t('billing:toasts.invoiceIssuedWithOverride', { number: issued.number ?? '' })
          : t('billing:toasts.invoiceIssued', { number: issued.number ?? '' }),
      );
    },
    onError: (error) => {
      setConfirmIssue(false);
      if (
        error instanceof ApiRequestError &&
        error.code === 'INVOICE_COMPLIANCE_DOCUMENTS_MISSING'
      ) {
        // R-001 : `details.missing` porte les types de pièces manquantes.
        const missing = error.details?.['missing'];
        setServerBlocked(Array.isArray(missing) ? (missing as string[]) : []);
        return;
      }
      // D-026 : la dérogation a été tentée sans la permission dédiée. Le
      // tiroir reste ouvert pour afficher le refus plutôt que de disparaître.
      if (error instanceof ApiRequestError && error.code === 'PERMISSION_DENIED') {
        toast.error(t('billing:compliance.override.denied'));
        return;
      }
      toast.error(errorMessage(error, t));
    },
  });

  const openPdf = useMutation({
    mutationFn: () => invoicePdf(invoiceId),
    onSuccess: (file) => {
      openDownloadedFile(file);
      toast.success(t('billing:toasts.pdfOpened'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  if (query.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={80} />
          <Skeleton height={480} />
        </div>
      </Container>
    );
  }

  if (query.isError || !invoice) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const isDraft = invoice.status === 'DRAFT';
  const actions = invoiceActions(invoice.status, session.can);
  const late = overdueDays(invoice);

  // Blocage R-001 : soit annoncé par la synthèse, soit renvoyé par l'émission.
  const summaryBlocked =
    summary.data?.canIssueInvoice === false &&
    summary.data.invoiceBlockedReason === 'COMPLIANCE_INCOMPLETE';
  const missingDocuments = serverBlocked ?? summary.data?.missingDocuments ?? [];
  const blocked = isDraft && (summaryBlocked || serverBlocked !== null);

  const previewProps = invoicePreviewProps(invoice, settings.data);
  const preview =
    isDraft && draft && liveTotals
      ? {
          ...previewProps,
          subject: draft.subject || null,
          notes: draft.notes || null,
          dueDate: draft.dueDate || null,
          totals: liveTotals,
          lines: draft.lines.map((line, index) => ({
            id: line.key,
            position: index + 1,
            kind: line.kind,
            label: line.label,
            description: line.description || null,
            quantity: line.quantity,
            unit: null,
            unitPrice: line.unitPrice,
            amount: Math.round(line.quantity * line.unitPrice),
            taxRuleCodes: [],
          })),
        }
      : previewProps;

  const documentBlock = (
    <BillingDocumentScroller>
      <BillingDocumentPreview {...preview} />
    </BillingDocumentScroller>
  );

  return (
    <Container>
      <PageHeader
        title={invoice.number ?? t('billing:status.invoice.DRAFT')}
        subtitle={
          invoice.number
            ? t('billing:invoices.detailSubtitle', {
                number: invoice.number,
                customer: invoice.billingParty.displayName,
              })
            : t('billing:invoices.draftSubtitle', {
                customer: invoice.billingParty.displayName,
                reference: invoice.reservationReference ?? '—',
              })
        }
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: t('billing:invoices.title'),
            link: <Link to="/billing/invoices">{t('billing:invoices.title')}</Link>,
          },
          { label: invoice.number ?? t('billing:status.invoice.DRAFT') },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            <Badge tone={invoiceTone(invoice.status)} size="lg" dot>
              {t(`billing:status.invoice.${invoice.status}`)}
            </Badge>
            {invoice.complianceOverridden ? (
              <Badge
                tone="amber"
                size="lg"
                title={t('billing:status.overriddenTitle', {
                  reason: invoice.complianceOverrideReason ?? '',
                  author: invoice.complianceOverrideByName ?? '',
                })}
              >
                <KeyRound className="ico ico-14" aria-hidden="true" />
                {t('billing:status.overridden')}
              </Badge>
            ) : null}
            {!isDraft ? (
              <Badge tone="neutral" size="lg">
                <Lock className="ico ico-14" aria-hidden="true" />
                {t('billing:status.immutable')}
              </Badge>
            ) : null}
          </div>
        }
      />

      <Card className="mb-4 no-print">
        <Toolbar style={{ borderBottom: 0, borderRadius: 'var(--r-md)' }}>
          <span className="strong display t-16">
            {formatAmount(invoice.totals.netTotal, i18n.language)}
          </span>
          <span className="spacer" />
          <Button variant="ghost" size="sm" onClick={() => window.print()}>
            <Printer className="ico ico-14" aria-hidden="true" />
            {t('billing:document.print')}
          </Button>
          {actions.downloadPdf ? (
            <Button
              variant="secondary"
              size="sm"
              loading={openPdf.isPending}
              onClick={() => openPdf.mutate()}
            >
              <Download className="ico ico-14" aria-hidden="true" />
              {t('billing:document.downloadPdf')}
            </Button>
          ) : null}
          {!isDraft ? (
            <Button
              variant="secondary"
              size="sm"
              title={t('billing:document.sendSoon')}
              onClick={() => setSendOpen(true)}
            >
              <Mail className="ico ico-14" aria-hidden="true" />
              {t('billing:document.sendEmail')}
            </Button>
          ) : null}
          {actions.recordPayment ? (
            <Button
              variant="secondary"
              size="sm"
              title={t('billing:actions.paymentSoon')}
              onClick={() => setPaymentOpen(true)}
            >
              <Banknote className="ico ico-14" aria-hidden="true" />
              {t('billing:actions.recordPayment')}
            </Button>
          ) : null}
          {actions.createCreditNote ? (
            <Button variant="danger" size="sm" onClick={() => setCreditNoteOpen(true)}>
              <RotateCcw className="ico ico-14" aria-hidden="true" />
              {t('billing:actions.createCreditNote')}
            </Button>
          ) : null}
          {actions.issue && !blocked ? (
            <Button variant="primary" size="sm" onClick={() => setConfirmIssue(true)}>
              <ShieldCheck className="ico ico-14" aria-hidden="true" />
              {t('billing:actions.issueInvoice')}
            </Button>
          ) : null}
        </Toolbar>
      </Card>

      {/* ---------------------- Bandeaux d'état du document -------------------- */}
      {invoice.complianceOverridden ? (
        <Notice
          tone="warning"
          className="mb-4 no-print"
          title={t('billing:invoices.overrideBanner.title')}
          icon={<KeyRound className="ico ico-20 notice__ico" aria-hidden="true" />}
          actions={
            invoice.reservationId ? (
              <Button variant="secondary" size="sm" asChild>
                <Link
                  to="/reservations/$reservationId/check-in"
                  params={{ reservationId: invoice.reservationId }}
                >
                  <Upload className="ico ico-14" aria-hidden="true" />
                  {t('billing:invoices.overrideBanner.completeFile')}
                </Link>
              </Button>
            ) : null
          }
        >
          {t('billing:invoices.overrideBanner.body', {
            reason: invoice.complianceOverrideReason ?? '',
            author: invoice.complianceOverrideByName ?? '',
          })}
        </Notice>
      ) : null}

      {!isDraft ? (
        <Notice
          tone="info"
          className="mb-4 no-print"
          icon={<Lock className="ico ico-20 notice__ico" aria-hidden="true" />}
          title={t('billing:invoices.immutableBanner.title')}
        >
          <p className="t-13 mb-2">{t('billing:invoices.immutableBanner.body')}</p>
          <dl className="dl dl--lines dl--left t-12" style={{ marginBottom: 0 }}>
            <dt>{t('billing:invoices.immutableBanner.issuedAt')}</dt>
            <dd>{invoice.issuedAt ? formatDateTime(invoice.issuedAt, i18n.language) : '—'}</dd>
            <dt>{t('billing:invoices.immutableBanner.issuedBy')}</dt>
            <dd>{invoice.issuedByName ?? '—'}</dd>
            {invoice.pdfSha256 ? (
              <>
                <dt>{t('billing:invoices.immutableBanner.hash')}</dt>
                <dd className="mono t-12" style={{ wordBreak: 'break-all' }}>
                  {invoice.pdfSha256}
                </dd>
              </>
            ) : null}
          </dl>
        </Notice>
      ) : null}

      {/* -------------------------- Blocage R-001 ---------------------------- */}
      {blocked && invoice.reservationId && summary.data ? (
        <div className="mb-5 no-print">
          <ComplianceBlockPanel
            reservationId={invoice.reservationId}
            reservationReference={invoice.reservationReference ?? ''}
            missingDocuments={missingDocuments}
            customerName={invoice.billingParty.displayName}
            guestName={invoice.guest?.displayName ?? null}
            amount={invoice.totals.netTotal}
            retrying={issue.isPending}
            onRetry={() => issue.mutate(undefined)}
            onOverride={() => setOverrideOpen(true)}
          />
        </div>
      ) : null}

      {/* --------------------------- Corps de page --------------------------- */}
      {isDraft && draft ? (
        <div className="grid grid--editor-preview">
          <div className="stack no-print">
            <Notice tone="info">{t('billing:document.previewLive')}</Notice>
            <BillingDraftEditor
              kind="INVOICE"
              value={draft}
              onChange={(next) => {
                setDraft(next);
                setDirty(true);
              }}
              totals={liveTotals ?? invoice.totals}
              canApplyDiscount={session.can('invoice.apply_discount')}
              {...(withholdingRule ? { withholdingRate: withholdingRule.rate } : {})}
            />
            <Card>
              <CardBody density="tight">
                <div className="panel-title">{t('billing:invoices.numberOnIssue')}</div>
                <p className="hint mb-0">{t('billing:invoices.numberOnIssueHint')}</p>
              </CardBody>
            </Card>
            <div className="row" style={{ gap: 8 }}>
              <Button
                variant="primary"
                loading={save.isPending}
                disabled={!dirty}
                onClick={() => save.mutate()}
              >
                <Save className="ico ico-16" aria-hidden="true" />
                {t('billing:editor.save')}
              </Button>
              {dirty ? (
                <span className="t-13" style={{ color: 'var(--warning)' }}>
                  {t('billing:editor.unsaved')}
                </span>
              ) : null}
            </div>
            {save.isError ? (
              <Notice tone="danger" title={t('common:states.errorTitle')}>
                {errorMessage(save.error, t)}
              </Notice>
            ) : null}
          </div>
          <div>{documentBlock}</div>
        </div>
      ) : (
        <div className="grid grid--main-aside">
          <div>{documentBlock}</div>

          <aside className="stack sticky-aside no-print">
            <Card>
              <CardHeader
                title={t('billing:invoices.settlement.title')}
                icon={<Wallet className="ico ico-16" aria-hidden="true" />}
                iconTone="amber"
              />
              <CardBody density="tight">
                <div className="money-strip" style={{ gridTemplateColumns: '1fr' }}>
                  <div className="money-strip__cell" style={{ padding: '12px 14px' }}>
                    <div className="money-strip__l">{t('billing:invoices.settlement.net')}</div>
                    <div className="money-strip__v t-20">
                      {formatAmount(invoice.totals.netTotal, i18n.language)}
                    </div>
                  </div>
                  <div
                    className="money-strip__cell money-strip__cell--paid"
                    style={{ padding: '12px 14px' }}
                  >
                    <div className="money-strip__l">{t('billing:invoices.settlement.paid')}</div>
                    <div className="money-strip__v t-20">
                      {formatAmount(invoice.totals.paidTotal, i18n.language)}
                    </div>
                  </div>
                  <div
                    className="money-strip__cell money-strip__cell--due"
                    style={{ padding: '12px 14px' }}
                  >
                    <div className="money-strip__l">{t('billing:invoices.settlement.balance')}</div>
                    <div className="money-strip__v t-20">
                      {formatAmount(invoice.totals.balance, i18n.language)}
                    </div>
                  </div>
                </div>
                {invoice.dueDate ? (
                  <p className="t-12 muted mt-3 mb-0">
                    {t('billing:invoices.settlement.dueOn', {
                      date: formatDateShort(invoice.dueDate, i18n.language),
                    })}
                    {late > 0
                      ? ` ${t('billing:invoices.settlement.overdue', { count: late })}`
                      : ''}
                  </p>
                ) : null}
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title={t('billing:invoices.payments.title')}
                icon={<Banknote className="ico ico-16" aria-hidden="true" />}
                iconTone="green"
              />
              <CardBody density="flush">
                {(payments.data ?? []).length === 0 ? (
                  <div className="card__body">
                    <p className="t-13 muted mb-0">{t('billing:invoices.payments.empty')}</p>
                  </div>
                ) : (
                  <TableWrap>
                    <Table compact>
                      <thead>
                        <tr>
                          <Th>{t('billing:invoices.payments.columns.reference')}</Th>
                          <Th>{t('billing:invoices.payments.columns.method')}</Th>
                          <Th align="right">{t('billing:invoices.payments.columns.amount')}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {(payments.data ?? []).map((payment) => (
                          <Tr key={payment.id}>
                            <Td className="t-13">
                              <Link
                                to="/billing/payments/$paymentId"
                                params={{ paymentId: payment.id }}
                              >
                                <span className="cell-id">{payment.reference}</span>
                              </Link>
                              <div className="t-12 muted">
                                {formatDateShort(payment.paidAt, i18n.language)}
                              </div>
                            </Td>
                            <Td className="t-13">
                              <Badge tone={methodTone(payment.method, payment.provider)}>
                                {payment.provider
                                  ? t(`payments:provider.${payment.provider}`)
                                  : t(`payments:method.${payment.method}`)}
                              </Badge>
                              {payment.status === 'REVERSED' ? (
                                <Badge
                                  tone={
                                    paymentBadge({ kind: 'PAYMENT', status: payment.status }).tone
                                  }
                                >
                                  {t('payments:status.REVERSED')}
                                </Badge>
                              ) : null}
                            </Td>
                            <Td align="right" numeric>
                              {formatAmount(payment.amount, i18n.language)}
                            </Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
                {actions.recordPayment ? (
                  <div className="card__foot">
                    <Button variant="secondary" size="sm" onClick={() => setPaymentOpen(true)}>
                      <Banknote className="ico ico-14" aria-hidden="true" />
                      {t('billing:actions.recordPayment')}
                    </Button>
                  </div>
                ) : null}
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title={t('billing:invoices.creditNotes.title')}
                icon={<RotateCcw className="ico ico-16" aria-hidden="true" />}
                iconTone="red"
              />
              <CardBody density="tight">
                {invoice.creditNotes.length === 0 ? (
                  <p className="t-13 muted mb-0">{t('billing:invoices.creditNotes.empty')}</p>
                ) : (
                  <ul
                    className="stack stack--sm"
                    style={{ listStyle: 'none', margin: 0, padding: 0 }}
                  >
                    {invoice.creditNotes.map((note) => (
                      <li key={note.id} className="row row--between">
                        <Link
                          to="/billing/credit-notes/$creditNoteId"
                          params={{ creditNoteId: note.id }}
                        >
                          <span className="cell-id">{note.number ?? '—'}</span>
                        </Link>
                        <span className="amount t-13">
                          −{formatAmount(note.netTotal, i18n.language)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </aside>
        </div>
      )}

      {/* ------------------------------ Dialogues ------------------------------ */}
      <Dialog open={confirmIssue} onOpenChange={setConfirmIssue}>
        <DialogContent
          title={t('billing:dialogs.issueInvoiceTitle')}
          description={t('billing:dialogs.issueInvoiceBody', { year: new Date().getFullYear() })}
          icon={<ShieldCheck className="ico ico-16" aria-hidden="true" />}
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirmIssue(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="primary"
                loading={issue.isPending}
                onClick={() => issue.mutate(undefined)}
              >
                {t('billing:actions.issueInvoice')}
              </Button>
            </>
          }
        />
      </Dialog>

      {invoice.reservationId ? (
        <ComplianceOverrideDrawer
          open={overrideOpen}
          onOpenChange={setOverrideOpen}
          reservationReference={invoice.reservationReference ?? ''}
          customerName={invoice.billingParty.displayName}
          amount={invoice.totals.netTotal}
          missingDocuments={missingDocuments}
          submitting={issue.isPending}
          onSubmit={(input) => issue.mutate(input)}
        />
      ) : null}

      <CreditNoteDrawer open={creditNoteOpen} onOpenChange={setCreditNoteOpen} invoice={invoice} />

      <PaymentDrawer
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        context={{
          customerId: invoice.billingParty.id,
          customerName: invoice.billingParty.displayName,
          invoiceId: invoice.id,
          invoiceNumber: invoice.number,
          invoiceBalance: invoice.totals.balance,
          ...(invoice.reservationId ? { reservationId: invoice.reservationId } : {}),
          reservationReference: invoice.reservationReference,
        }}
      />

      <SendDocumentDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        kind="invoices"
        documentId={invoice.id}
        documentLabel={invoice.number ?? invoice.id}
        defaultRecipient={invoice.billingParty.email ?? null}
      />

      {invoice.status === 'CANCELLED' ? (
        <EmptyState
          className="no-print mt-4"
          icon={<RotateCcw className="ico ico-24" aria-hidden="true" />}
          title={t('billing:status.invoice.CANCELLED')}
          description={t('billing:invoices.creditNotes.title')}
        />
      ) : null}
    </Container>
  );
}
